import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Construction as BuildingIcon, Groups as UsersIcon } from "@mui/icons-material";
import LiveWheelDisplay from "../Common/LiveWheelDisplay";
import { HOL, holPulse, holShimmer } from "../UserSide/pages/holTheme";

const Countdown = () => {
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (count <= 0) return undefined;
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
      {count > 0 ? (
        <Typography
          key={count}
          sx={{
            fontFamily: HOL.fontDisplay,
            fontWeight: 900,
            fontSize: { xs: "6rem", sm: "8rem" },
            lineHeight: 1,
            background: `linear-gradient(135deg, ${HOL.gold}, ${HOL.lower})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 28px rgba(245,215,110,0.35))",
          }}
        >
          {count}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontFamily: HOL.fontDisplay,
            fontWeight: 900,
            fontSize: { xs: "2.4rem", sm: "3.4rem" },
            lineHeight: 1,
            color: HOL.gold,
          }}
        >
          LET&apos;S GO!
        </Typography>
      )}
    </Box>
  );
};

const HolUserWheelStage = ({ userWheel, size, preview = false }) => {
  const phase = userWheel?.phase || null;
  const [animComplete, setAnimComplete] = useState(false);
  const [wheelSize, setWheelSize] = useState(size || 300);
  const spinLockRef = useRef(null);

  const spinToken = phase === "spinning" ? userWheel?.token : null;
  if (spinToken && userWheel?.spin && spinLockRef.current?.token !== spinToken) {
    spinLockRef.current = {
      token: spinToken,
      finalAngle: userWheel.spin.finalAngle,
      spinDuration: userWheel.spin.spinDuration,
      winner: userWheel.spin.winner,
    };
  }
  if (!spinToken) spinLockRef.current = null;
  const lockedSpin = spinLockRef.current;

  useEffect(() => {
    setAnimComplete(false);
  }, [lockedSpin?.token, phase]);

  useEffect(() => {
    if (size) {
      setWheelSize(size);
      return undefined;
    }
    const fit = () => {
      const w = Math.min(window.innerWidth - 36, 420);
      const h = Math.min(window.innerHeight - 240, 420);
      setWheelSize(Math.max(220, Math.min(w, h)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [size]);

  const onAnimationEnd = useCallback(() => setAnimComplete(true), []);

  if (!phase) return null;

  if (phase === "building" && !preview) {
    return (
      <Box sx={{ textAlign: "center", py: { xs: 3, sm: 4 }, px: 2 }}>
        <BuildingIcon
          sx={{
            fontSize: 48,
            color: HOL.gold,
            mb: 1.5,
            animation: `${holPulse} 1.6s ease-in-out infinite`,
          }}
        />
        <Typography
          sx={{
            fontFamily: HOL.fontDisplay,
            fontWeight: 800,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            mb: 1,
            background: `linear-gradient(90deg, ${HOL.gold}, ${HOL.lower})`,
            backgroundSize: "200% auto",
            animation: `${holShimmer} 3s linear infinite`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Building The Users Wheel…
        </Typography>
        <Typography sx={{ color: HOL.muted, fontSize: "0.92rem", lineHeight: 1.55, maxWidth: 420, mx: "auto" }}>
          The host is adding players from the queue. Hang tight — the spin is coming soon!
        </Typography>
      </Box>
    );
  }

  if (phase === "countdown") {
    return <Countdown key={userWheel.token || "countdown"} />;
  }

  const entries = userWheel.entries || [];
  const showWheel = phase === "ready" || phase === "spinning" || (preview && phase === "building");
  if (!showWheel) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: HOL.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: "1.15rem", sm: "1.4rem" },
          mb: 0.5,
        }}
      >
        Who Gets the Hot Seat?
      </Typography>
      <Chip
        label="SELECTION WHEEL"
        size="small"
        icon={<UsersIcon sx={{ fontSize: "14px !important" }} />}
        sx={{
          mb: 0.75,
          height: 24,
          fontWeight: 800,
          fontSize: "0.68rem",
          letterSpacing: "0.06em",
          bgcolor: "rgba(255,107,61,0.16)",
          color: HOL.lower,
          border: `1px solid ${HOL.lower}55`,
          "& .MuiChip-icon": { color: HOL.lower },
        }}
      />
      <Typography sx={{ color: HOL.muted, mb: 1.5, fontSize: "0.85rem", maxWidth: 420 }}>
        {phase === "spinning"
          ? "Watch the wheel — the hot seat is about to be chosen."
          : phase === "building"
            ? "Wheel preview. New players are added automatically. Players still see “Building The Users Wheel…” until the countdown finishes."
            : "The host is about to spin the queue."}
      </Typography>
      <LiveWheelDisplay
        entries={entries}
        spinning={phase === "spinning"}
        finalAngle={lockedSpin?.finalAngle || 0}
        spinDuration={lockedSpin?.spinDuration || 6}
        winnerEntry={lockedSpin?.winner || null}
        animationComplete={animComplete}
        onAnimationEnd={onAnimationEnd}
        size={wheelSize}
      />
      {animComplete && lockedSpin?.winner && (
        <Typography
          sx={{
            mt: 1.5,
            fontFamily: HOL.fontDisplay,
            fontWeight: 800,
            color: HOL.gold,
            fontSize: { xs: "1.05rem", sm: "1.2rem" },
          }}
        >
          {lockedSpin.winner.display_name} is in the Hot Seat!
        </Typography>
      )}
    </Box>
  );
};

export default HolUserWheelStage;
